import React, { FC, PropsWithChildren, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DialogPlan from "../Dialog/DialogPlan";
import { newFeatureDetails } from "../Dialog/PlanFeatures";
// material-ui
import {
  Box,
  Grid,
  Button,
  Modal,
} from "@mui/material";
import {
  IAccountBasicFormInputs,
  ISubscriptions,
} from "../../utils/account.types";
import axios from "../../utils/axios";
import { IOptions } from "../FormFields/CustomSelect";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { useAppSelector } from "../../store/hooks";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  data?: any;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 880,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

//Custom Setting Dialog Component.
const SettingDialog: FC<PropsWithChildren<IDialogProps>> = ({
  title,
  onClose,
  open,
  children,
  onSubmit,
  data,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [subscriptionOptions, setSubscriptionOptions] = useState<IOptions[]>(
    []
  );
  const [featuresData, setfeaturesData] = useState({});
  const { status } = useAppSelector((state) => state.account);
  const { register } = useForm({});

  //Fetch Subscriptions on Account form load.
  useEffect(() => {
    let isMounted = true;
    // Method to fetch available subscriptions.
    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get<ISubscriptions>(
          "/api/v1/subscription"
        );
        const { subscriptions = [] } = response.data;

        const options = subscriptions.map(({ _id, plan }) => ({
          label: plan.toUpperCase(),
          value: _id,
        }));

        let featureDetails: Array<any> = [];

        subscriptions.map(({ _id, resources }) => {
          let featuesOptions = resources.map(({ action, displayName }) => {
            return action + ' ' + displayName;
          })
          featureDetails.push(featuesOptions);
          return featureDetails
        });

        setfeaturesData(newFeatureDetails)

        if (isMounted) {
          setSubscriptionOptions(options);
        }
      } catch (error: any) {
        // Empty catch block.
      }
    };
    fetchSubscriptions();
    return () => {
      isMounted = false;
    };
  }, []);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({});

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    const subscriptionPlan = subscriptionOptions.find((s, i) => {
      return tabValue === i;
    });
    if (subscriptionPlan?.value) {
      form.append("subscription", subscriptionPlan.value);
    }
    onSubmit(form);
  };

  useEffect(() => {
    if (data && subscriptionOptions.length) {
      let tabIndex = subscriptionOptions.findIndex(x => x.label.toLowerCase() === data.subscription['plan'].toLowerCase());
      setTabValue(tabIndex)
    }
  }, [subscriptionOptions, data]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <FormProvider {...methods}>
        <Box 
          sx={style} 
          component="form"
          onSubmit={methods.handleSubmit(innerSubmit)}
          noValidate
        >
        {/* Header with title and action buttons - matching Payment Update style */}
        <Grid container>
          <Grid 
            xs={12} 
            sm={12} 
            md={12} 
            lg={12} 
            p={2} 
            pl={2} 
            bgcolor="#e9ecf1" 
            style={{ 
              borderTopLeftRadius: 8, 
              borderTopRightRadius: 8, 
              height: 50 
            }}
          >
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <LoadingButton
                type="submit"
                loading={status === ApiStatus.LOADING}
                style={{ 
                  color: '#488dff', 
                  backgroundColor: '#fff', 
                  border: '1px solid #488dff', 
                  marginRight: 10 
                }}
                variant="contained"
                size="small"
              >
                Update
              </LoadingButton>

              <Button
                style={{ 
                  color: '#488dff', 
                  backgroundColor: '#fff', 
                  border: '1px solid #488dff', 
                  marginRight: 10 
                }}
                variant="contained"
                size="small"
                onClick={() => {
                  methods.reset();
                  onClose();
                }}
              >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>{title}</h3>
          </Grid>
        </Grid>

        {/* Content Area */}
        <Grid container p={2} spacing={2}>
          <Grid item xs={12} md={12} sm={12}>
            <Box 
              sx={{ 
                mt: 2, 
                backgroundColor: "#fafafb", 
                borderRadius: "8px", 
                height: 495,
                border: '1.5px solid #e9ecf1'
              }}
            >
              <DialogPlan
                featuresData={featuresData}
                subscriptionOptions={subscriptionOptions}
                tabValue={tabValue}
                handleTabs={(val: any) => handleTabChange(val)}
                register={register}
              />
            </Box>
          </Grid>
          {children}
        </Grid>
              </Box>
      </FormProvider>
    </Modal>
  );
};

export default SettingDialog;